/**
 * @GestionalePD Admin campaign details UI
 */
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { Helmet } from 'react-helmet-async';
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import {
  Card,
  Stack,
  Container,
  Typography,
  Tabs,
  Tab,
  Box,
  Grid,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableContainer,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Radio,
  RadioGroup,
  FormLabel,
  FormControl,
  FormControlLabel,
  MenuItem,
  TableCell,
  TablePagination,
  TableFooter,
  Tooltip,
  InputAdornment,
  Select,
  InputLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import {
  Assignment as AssignmentIcon,
  PriceChange as PriceChangeIcon,
  DoDisturbOn as DoDisturbOnIcon,
} from '@mui/icons-material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

// -----------------------------------------------------------------------------------------------------
import { usePopup } from '../../../../context/PopupContext';
import {
  DefaultContainedButton,
  DefaultOutlinedButton,
  RedOutlinedButton,
  CustomIconButton,
} from '../../../../utils/Buttons';
import { validatePhoneNumber } from '../../../../utils/numberValidity';
import { CustomAutocomplete, CustomTextField, CustomDatePicker } from '../../../../utils/StyledInputs';

import { TablePaginationActions, theme } from '../../../../utils/TableStyles';
import { ErrorUI, HttpErrorHandler } from '../../../../utils/ErrorHandler';
import { MultipleTabPanel, a11yProps } from '../../../../utils/TabStyles';
import { entityTypeLabel } from '../../../../utils/EntityTypeLabel';
import { DecisionDialog } from '../../../../utils/DecisionDialog';
import { LoadingUI } from '../../../../utils/LayoutStyles';
import currencies from '../../../../model/currencies';
import { EntitiesDialog } from '../../../../utils/EntitiesDialog';
import API from '../../../../api';

dayjs.locale('it');

/**
 * @returns Admin Campaign details page UI
 */
export default function CampaignDetails() {
  // general
  const is600Screen = useMediaQuery('(min-width: 600px)');
  const navigate = useNavigate();
  const { state } = useLocation();
  const campaignID = state.campaignID;
  const [errorMessage, setErrorMessage] = useState('');
  const [currentProfile, setCurrentProfile] = useState('');

  const { showPopup, setPopUpText } = usePopup();

  /* ---------------------------------- Tabs ---------------------------------- */
  const [tabValue, setTabValue] = useState(0);

  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };

  //  Filter Tabs
  const [value, setValue] = useState(0);
  const handleChangeFilterTab = (event, newValue) => {
    setValue(newValue);
  };

  /* ------------------------ Get Current Campaign Info ----------------------- */
  const [entities, setEntities] = useState([]);
  const [positions, setPositions] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  const [campaignId, setCampaignId] = useState('');
  const [campaignInitialUrl, setCampaignInitialUrl] = useState('');
  const [campaignDescription, setCampaignDescription] = useState('');
  const [campaignPrefix, setCampaignPrefix] = useState('');
  const [campaignStartDate, setCampaignStartDate] = useState('');
  const [campaignEndDate, setCampaignEndDate] = useState('');
  const [campaignStartRegDate, setCampaignStartRegDate] = useState('');
  const [campaignEndRegDate, setCampaignEndRegDate] = useState('');
  const [campaignActive, setCampaignActive] = useState('');
  const [campaignType, setCampaignType] = useState('PD');
  const [canCreateCampaign, setCanCreateCampaign] = useState(false);
  const [canDeleteCampaign, setCanDeleteCampaign] = useState(false);
  const [rateTypes, setRateTypes] = useState([]);
  const [apiCalled, setApiCalled] = useState(false);
  const [staticDataApiCalled, setStaticDataApiCalled] = useState(false);
  const [ratesTypesCalled, setRatesTypesCalled] = useState(false);
  const [positionsCalled, setPositionsCalled] = useState(false);
  const [entityTypesCalled, setEntityTypesCalled] = useState(false);
  const [edit, setEdit] = useState(false);

  const [openEntitiesFilter, setOpenEntitiesFilter] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);

  useEffect(() => {
    if (selectedEntity && openNewRate) {
      setNewRateEntity(selectedEntity);
    } else if (selectedEntity && openEditRate) {
      setRateToEditEntity(selectedEntity);
    }
  }, [selectedEntity]);

  // get api calls for campaign data
  useEffect(() => {
    getCurrentCampaign();
    getBlacklistedEntities();
  }, []);

  // Get RateTypes Api
  const getRatesTypes = () => {
    if (rateTypes.length === 0) {
      API.get('rate_types')
        .then((response) => {
          setRateTypes(response.data);
          setRatesTypesCalled(true);
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    }
  };

  // Get Positions Api
  const getPositionsList = (rateLoading) => {
    if (positions.length === 0) {
      API.get('/institutional_positions')
        .then((response) => {
          setPositions(response.data.institutional_positions);
          setPositionsCalled(true);

          if (rateLoading) {
            setRateToEditLoading(false);
          }
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    }
  };

  const getEntityTypesList = (rateLoading) => {
    if (entityTypes.length === 0) {
      API.get('/etypes')
        .then((response) => {
          setEntityTypes(response.data.entity_types);
          setEntityTypesCalled(true);
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    }

    if (rateLoading) {
      setRateToEditLoading(false);
    }
  };

  // Get Positions Entities and RatesTypes
  const getStaticData = () => {
    getRatesTypes();
    getEntityTypesList(false);
    getPositionsList(false);
  };

  // Set True Api Called Status
  useEffect(() => {
    if (ratesTypesCalled && positionsCalled && entityTypesCalled) {
      setStaticDataApiCalled(true);
    }
  }, [ratesTypesCalled, positionsCalled, entityTypesCalled]);

  // Get Current Campaign
  const getCurrentCampaign = () => {
    API.get(`/campaigns/${campaignID}?expand=active_rates`)
      .then((response) => {
        setCampaignId(campaignID);
        setCampaignDescription(response.data.description);
        setCampaignPrefix(response.data.prefix);
        setCampaignInitialUrl(response.data.description);
        setCampaignStartDate(response.data.start_date);
        setCampaignEndDate(response.data.end_date);
        setCampaignStartRegDate(response.data.start_registrations_date);
        setCampaignEndRegDate(response.data.end_registrations_date);
        setCampaignActive(response.data.active);
        setCampaignType(response.data.campaign_type || 'PD');
        setRates(response.data.campaign_rates);
        filterRates(response.data.campaign_rates);
        setCanCreateCampaign(response.data.auth_type === 'W' || response.data.auth_type === 'D');
        setCanDeleteCampaign(response.data.auth_type === 'D');
        setApiCalled(true);
        setRatesApiCalled(true);
      })
      .catch((error) => {
        setApiCalled(true);
        HttpErrorHandler(error, setErrorMessage);
      });
  };

  /* ------------------------------ Edit Campaign ----------------------------- */

  // Change Active Campaign
  const handleChangeActive = (e) => {
    setCampaignActive(Number(e.target.value));
  };

  // Handle Close Campaign Data Edit Button
  const handleCloseEditButtonClick = () => {
    setEdit(false);
  };

  // Submit Campaign Modify
  const submitModify = (e) => {
    e.preventDefault();
    setEdit(true);

    if (edit) {
      const updatedCampaign = {
        description: campaignDescription,
        start_date: dayjs(campaignStartDate).format('YYYY/MM/DD'),
        end_date: dayjs(campaignEndDate).format('YYYY/MM/DD'),
        start_registrations_date: dayjs(campaignStartRegDate).format('YYYY/MM/DD'),
        end_registrations_date: dayjs(campaignEndRegDate).format('YYYY/MM/DD'),
        active: campaignActive,
        campaign_type: campaignType,
        prefix: campaignPrefix,
      };

      let itemsLeft = false;

      Object.keys(updatedCampaign).forEach((key) => {
        const value = updatedCampaign[key];

        if (Object.prototype.hasOwnProperty.call(updatedCampaign, key)) {
          if (
            value === undefined ||
            value === null ||
            value === 'Invalid Date' ||
            (typeof value === 'string' && value.length === 0)
          ) {
            itemsLeft = true;
            showPopup('warning', 'Inserisci i dati mancanti');
          }
        }
      });

      if (!itemsLeft) {
        if (dayjs(updatedCampaign.end_date).isBefore(dayjs(updatedCampaign.start_date))) {
          showPopup(false, 'La data di fine non può essere antecedente a quella di inizio');
        } else if (
          dayjs(updatedCampaign.end_registrations_date).isBefore(dayjs(updatedCampaign.start_registrations_date))
        ) {
          showPopup(false, 'La data di fine delle registrazioni non può essere antecedente a quella di inizio');
        } else {
          API.put(`/campaigns/${campaignId}`, updatedCampaign)
            .then((response) => {
              showPopup(true, 'Informazioni salvate');
            })
            .catch((error) => {
              HttpErrorHandler(error, setPopUpText);
              showPopup(false);
            });

          setEdit(false);
        }
      }
    }
  };

  /* ---------------------------  Delete Campaign --------------------------- */
  const [openDeleteCampaign, setOpenDeleteCampaign] = useState(false);

  // Open Delete Campaign Modal
  const handleOpenDeleteCampaign = () => {
    setOpenDeleteCampaign(true);
  };
  // Close Delete Campaign Modal
  const handleCloseDeleteCampaign = () => {
    setOpenDeleteCampaign(false);
  };

  // Delete Campaign
  const deleteCampaign = () => {
    API.delete(`/campaigns/${campaignId}`)
      .then((response) => {})
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
    navigate('/admin/campaigns');
  };

  /* ----------------------- Get Current Campgign Rates ----------------------- */

  const [rates, setRates] = useState([]);

  // Filtered Rates
  const [positionRates, setPositionRates] = useState([]);
  const [entityRates, setEntityRates] = useState([]);
  const [freeRates, setFreeRates] = useState([]);
  const [displayedRates, setDisplayedRates] = useState([]);
  const [ratesApiCalled, setRatesApiCalled] = useState(false);

  // Camprates Api
  const getCampRates = (editRate) => {
    if (campaignId) {
      API.get(`/campaigns/${campaignId}?expand`)
        .then((response) => {
          setRates(response.data.campaign_rates);
          filterRates(response.data.campaign_rates);
          setRatesApiCalled(true);

          if (editRate) {
            showPopup(true, 'Informazioni salvate');
            setTimeout(() => {
              handleCloseEditRate();
            }, 2000);
          }
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    }
  };

  // Rates Filter
  const filterRates = (responseRates) => {
    const PositionRates = [];
    const EntityRates = [];
    const FreeRates = [];

    responseRates.forEach((rate) => {
      switch (rate.rate_type.name) {
        case 'C':
          PositionRates.push(rate);
          break;
        case 'E':
          EntityRates.push(rate);
          break;
        case 'L':
          FreeRates.push(rate);
          break;
        case 'ET':
          EntityRates.push(rate);
          break;
        default:
          break;
      }
    });
    setPositionRates(PositionRates);
    setEntityRates(EntityRates);
    setFreeRates(FreeRates);
  };

  // Change Displayed Rates
  useEffect(() => {
    setPage(0);
    switch (value) {
      case 0:
        setDisplayedRates(rates);
        break;
      case 1:
        setDisplayedRates(positionRates);
        break;
      case 2:
        setDisplayedRates(entityRates);
        break;
      case 3:
        setDisplayedRates(freeRates);
        break;
      default:
        break;
    }
  }, [value, positionRates, entityRates, freeRates, rates]);

  /* ------------------------------ Import Rates ------------------------------ */
  const [openImportRates, setOpenImportRates] = useState(false);
  const [campaignList, setCampaignList] = useState([]);
  const [campaignListApiCalled, setCampaignListApiCalled] = useState(false);
  const [selectedImportCampaign, setSelectedImportCampaign] = useState({});

  // Open New Rate Modal
  const handleOpenImportRates = () => {
    setOpenImportRates(true);
    getCampaignList();
  };
  // Close New Rate Modal
  const handleCloseImportRates = () => {
    setOpenImportRates(false);
    setSelectedImportCampaign(null);
  };

  const getCampaignList = () => {
    API.get('/campaigns')
      .then((response) => {
        setCampaignList(response.data.data);
        setCampaignListApiCalled(true);
      })
      .catch((error) => {
        setCampaignListApiCalled(true);
        HttpErrorHandler(error, setErrorMessage);
      });
  };

  const importRates = (e) => {
    e.preventDefault();

    API.post(`/camprates_import?new_campaign_id=${campaignId}&old_campaign_id=${selectedImportCampaign}`)
      .then((response) => {
        showPopup(true, response.data.message);
        getCurrentCampaign();
        handleCloseImportRates();
      })
      .catch((error) => {
        HttpErrorHandler(error, setErrorMessage);
      });
  };

  /* ------------------------------- New Rate ------------------------------ */
  const [openNewRate, setOpenNewRate] = useState(false);
  const [newRateName, setNewRateName] = useState('');
  const [newRatePrice, setNewRatePrice] = useState('');
  const [newRateDiscountedPrice, setNewRateDiscountedPrice] = useState('');
  const [newRateCurr, setNewRateCurr] = useState('EUR');
  const [newRateEntity, setNewRateEntity] = useState('');
  const [newRatePosition, setNewRatePosition] = useState('');
  const [newRateModifiable, setNewRateModifiable] = useState(0);
  const [newRateEntityType, setNewRateEntityType] = useState('');
  const [newRateType, setNewRateType] = useState({});

  // Set Price and Modifiable if RateType is Free
  useEffect(() => {
    if (newRateType) {
      if (newRateType.name === 'L') {
        setNewRatePrice('0.00');
        setNewRateModifiable(1);
      } else {
        setNewRatePrice('');
        setNewRateModifiable(0);
      }
    }
    setNewRateName('');
  }, [newRateType]);

  useEffect(() => {
    if (newRatePosition !== '' && newRatePosition !== null && newRatePosition !== undefined) {
      setNewRateName(newRatePosition.name);
    } else if (newRateEntity !== '' && newRateEntity !== null && newRateEntity !== undefined) {
      setNewRateName(newRateEntity.name);
    } else if (newRateEntityType !== '' && newRateEntityType !== null && newRateEntityType !== undefined) {
      setNewRateName(newRateEntityType.name);
    }
  }, [newRatePosition, newRateEntity, newRateEntityType]);

  // Empty Position and Entity Fields
  useEffect(() => {
    setNewRatePosition(null);
    setNewRateEntity(null);
    setNewRateEntityType(null);
  }, [newRateType]);

  // Open New Rate Modal
  const handleOpenNewRate = () => {
    setOpenNewRate(true);
    getStaticData();
  };
  // Close New Rate Modal
  const handleCloseNewRate = () => {
    setOpenNewRate(false);
    setNewRateName('');
    setNewRatePrice('');
    setNewRateDiscountedPrice('');
    setNewRateCurr('EUR');
    setNewRateType('');
    setNewRateEntity('');
    setNewRateEntityType('');
    setSelectedEntity(null);
    setNewRatePosition('');
    setNewRateModifiable(0);
  };
  // New Rate Modifiable
  const handleNewRateModifiable = (e) => {
    setNewRateModifiable(e.currentTarget.value);
  };

  // Submit New Rate
  const newRate = (e) => {
    e.preventDefault();

    const newRate = {
      campaign_id: campaignId,
      name: newRateName,
      price: newRatePrice,
      price_discounted: newRateDiscountedPrice,
      currency: newRateCurr,
      has_editable: newRateModifiable,
      rate_type: newRateType.id,
    };

    if (newRatePosition) {
      newRate.position_id = newRatePosition.id;
    }

    if (newRateEntity) {
      newRate.entity_id = newRateEntity.id;
    }

    if (newRateEntityType) {
      newRate.entity_type_id = newRateEntityType.id;
    }

    let itemsLeft = false;

    // Missing Data Snackbar Conditions
    if (newRateType.name === 'L') {
      if (
        (newRate.campaign_id !== null &&
          newRate.campaign_id !== undefined &&
          newRate.campaign_id.length > 0 &&
          newRate.name !== null &&
          newRate.name !== undefined &&
          newRate.name.length > 0 &&
          newRate.price !== null &&
          newRate.price !== undefined &&
          newRate.price.length > 0 &&
          newRate.currency !== null &&
          newRate.currency !== undefined &&
          newRate.currency.length > 0 &&
          newRate.has_editable !== null &&
          newRate.has_editable !== undefined) === false
      ) {
        itemsLeft = true;
        showPopup('warning', 'Inserisci i dati mancanti');
      }
    } else if (newRateType.name === 'E') {
      if (
        (newRate.campaign_id !== null &&
          newRate.campaign_id !== undefined &&
          newRate.campaign_id.length > 0 &&
          newRate.name !== null &&
          newRate.name !== undefined &&
          newRate.name.length > 0 &&
          newRate.price !== null &&
          newRate.price !== undefined &&
          newRate.price.length > 0 &&
          newRate.price_discounted !== null &&
          newRate.price_discounted !== undefined &&
          newRate.price_discounted.length > 0 &&
          newRate.currency !== null &&
          newRate.currency !== undefined &&
          newRate.currency.length > 0 &&
          newRate.entity_id !== null &&
          newRate.entity_id !== undefined &&
          newRate.entity_id.length > 0 &&
          newRate.has_editable !== null &&
          newRate.has_editable !== undefined) === false
      ) {
        itemsLeft = true;
        showPopup('warning', 'Inserisci i dati mancanti');
      }
    } else if (newRateType.name === 'C') {
      if (
        (newRate.campaign_id !== null &&
          newRate.campaign_id !== undefined &&
          newRate.campaign_id.length > 0 &&
          newRate.name !== null &&
          newRate.name !== undefined &&
          newRate.name.length > 0 &&
          newRate.price !== null &&
          newRate.price !== undefined &&
          newRate.price.length > 0 &&
          newRate.price_discounted !== null &&
          newRate.price_discounted !== undefined &&
          newRate.price_discounted.length > 0 &&
          newRate.currency !== null &&
          newRate.currency !== undefined &&
          newRate.currency.length > 0 &&
          newRate.position_id !== null &&
          newRate.position_id !== undefined &&
          newRate.position_id.length > 0 &&
          newRate.has_editable !== null &&
          newRate.has_editable !== undefined) === false
      ) {
        itemsLeft = true;
        showPopup('warning', 'Inserisci i dati mancanti');
      }
    } else if (newRateType.name === 'ET') {
      if (
        (newRate.campaign_id !== null &&
          newRate.campaign_id !== undefined &&
          newRate.campaign_id.length > 0 &&
          newRate.name !== null &&
          newRate.name !== undefined &&
          newRate.name.length > 0 &&
          newRate.price !== null &&
          newRate.price !== undefined &&
          newRate.price.length > 0 &&
          newRate.price_discounted !== null &&
          newRate.price_discounted !== undefined &&
          newRate.price_discounted.length > 0 &&
          newRate.currency !== null &&
          newRate.currency !== undefined &&
          newRate.currency.length > 0 &&
          newRate.entity_type_id !== null &&
          newRate.entity_type_id !== undefined &&
          newRate.entity_type_id.length > 0 &&
          newRate.has_editable !== null &&
          newRate.has_editable !== undefined) === false
      ) {
        itemsLeft = true;
        showPopup('warning', 'Inserisci i dati mancanti');
      }
    } else if (
      (newRate.campaign_id !== null &&
        newRate.campaign_id !== undefined &&
        newRate.campaign_id.length > 0 &&
        newRate.name !== null &&
        newRate.name !== undefined &&
        newRate.name.length > 0 &&
        newRate.rate_type !== null &&
        newRate.rate_type !== undefined &&
        newRate.rate_type.length > 0 &&
        newRate.price !== null &&
        newRate.price !== undefined &&
        newRate.price.length > 0 &&
        newRate.price_discounted !== null &&
        newRate.price_discounted !== undefined &&
        newRate.price_discounted.length > 0 &&
        newRate.currency !== null &&
        newRate.currency !== undefined &&
        newRate.currency.length > 0 &&
        newRate.has_editable !== null &&
        newRate.has_editable !== undefined) === false
    ) {
      itemsLeft = true;
      showPopup('warning', 'Inserisci i dati mancanti');
    }

    if (!itemsLeft) {
      API.post(`/camprates`, newRate)
        .then((response) => {
          setNewRateName('');
          setNewRatePrice('');
          setNewRateType('');
          setNewRateEntity('');
          setNewRatePosition('');
          setNewRateModifiable('');
          getCampRates(false);
          handleCloseNewRate();
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    }
  };

  /* -------------------------------- Edit Rate ------------------------------- */
  const [openEditRate, setOpenEditRate] = useState(false);
  const [rateToEdit, setRateToEdit] = useState({});
  const [rateToEditRateTypeDescription, setRateToEditRateTypeDescription] = useState('');
  const [rateToEditName, setRateToEditName] = useState('');
  const [rateToEditPrice, setRateToEditPrice] = useState('');
  const [rateToEditDiscountedPrice, setRateToEditDiscountedPrice] = useState('');
  const [rateToEditCurr, setRateToEditCurr] = useState('');
  const [rateToEditModifiable, setRateToEditModifiable] = useState('');
  const [rateToEditType, setRateToEditType] = useState('');
  const [rateToEditEntity, setRateToEditEntity] = useState({});
  const [rateToEditPosition, setRateToEditPosition] = useState({});
  const [rateToEditEntityType, setRateToEditEntityType] = useState('');
  const [rateToEditLoading, setRateToEditLoading] = useState(false);

  // Open Edit Rate Modal
  const handleOpenEditRate = (rate) => {
    if (
      (rate.rate_type.name === 'C' && positions.length === 0) ||
      (rate.rate_type.name === 'E' && entities.length === 0) ||
      (rate.rate_type.name === 'ET' && entities.length === 0)
    ) {
      setRateToEditLoading(true);
    }
    setOpenEditRate(true);
    setRateToEditType(rate.rate_type);
    setRateToEditName(rate.name);
    setRateToEditPrice(rate.price);
    setRateToEditDiscountedPrice(rate.price_discounted);
    setRateToEditCurr(rate.currency);
    setRateToEditModifiable(rate.has_editable);
    setRateToEdit(rate);
    setRateToEditRateTypeDescription(rate.rate_type.description);

    if (rate.rate_type.name === 'C') {
      setRateToEditPosition(rate.position);
    } else if (rate.rate_type.name === 'E') {
      setRateToEditEntity(rate.entity);
    } else if (rate.rate_type.name === 'ET') {
      setRateToEditEntityType(rate.entity_type);
    }
  };

  useEffect(() => {
    if (rateToEditLoading) {
      if (Object.keys(rateToEditPosition).length > 0 && rateToEditType.name === 'C') {
        getPositionsList(true);
      } else if (Object.keys(rateToEditEntity).length > 0 && rateToEditType.name === 'E') {
        setRateToEditLoading(false);
      } else if (rateToEditType.name === 'ET') {
        getEntityTypesList(true);
      }
    }
  }, [rateToEditLoading, rateToEditType, rateToEditPosition, rateToEditEntity, rateToEditEntityType]);

  // Close Edit Rate Modal
  const handleCloseEditRate = () => {
    setOpenEditRate(false);
    setRateToEditCurr('');
    setRateToEditEntity('');
    setRateToEditPosition('');
    setRateToEditModifiable('');
    setRateToEditRateTypeDescription('');
    setRateToEditName('');
    setRateToEditPrice('');
    setRateToEditDiscountedPrice('');
    setRateToEditType('');
    setRateToEditEntityType('');
    setRateToEdit({});
  };

  // Edit Rate Modifiable
  const handleEditRateModifiable = (e) => {
    setRateToEditModifiable(e.currentTarget.value);
  };

  // Submit Edit Rate
  const editRate = (e) => {
    e.preventDefault();
    const updatedRate = {
      campaign_id: rateToEdit.campaign_id,
      rate_type: rateToEditType.id,
      name: rateToEditName,
      price: rateToEditPrice,
      price_discounted: rateToEditDiscountedPrice,
      currency: rateToEditCurr,
      has_editable: rateToEditModifiable,
    };

    if (Object.keys(rateToEditEntity).length > 0) {
      updatedRate.entity_id = rateToEditEntity.id;
      updatedRate.position_id = '';
      updatedRate.entity_type_id = '';
    } else if (Object.keys(rateToEditPosition).length > 0) {
      updatedRate.position_id = rateToEditPosition.id;
      updatedRate.entity_id = '';
      updatedRate.entity_type_id = '';
    } else if (Object.keys(rateToEditEntityType).length > 0) {
      updatedRate.entity_type_id = rateToEditEntityType.id;
      updatedRate.position_id = '';
      updatedRate.entity_id = '';
    }

    API.put(`/camprates/${rateToEdit.id}`, updatedRate)
      .then((response) => {
        getCampRates(true);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  /* ------------------------------ Delete Rate ------------------------------ */

  const [openDeleteRate, setOpenDeleteRate] = useState(false);
  const [rateToDelete, setRateToDelete] = useState({});

  // Open Delete Rates Dialog
  const handleOpenDeleteRate = (rate) => {
    setRateToDelete(rate);
    setOpenDeleteRate(true);
  };

  // Close Delete Rates Dialog
  const handleCloseDeleteRate = () => {
    setOpenDeleteRate(false);
    setRateToDelete({});
  };

  // Submit Delete Rate
  const handleDeleteRate = async (e) => {
    e.preventDefault();

    API.delete(`/camprates/${rateToDelete.id}`)
      .then((response) => {
        handleCloseDeleteRate();
        getCampRates(false);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  /* ------------------------------- Black List ------------------------------- */

  const [blacklistedEntities, setBlacklistedEntities] = useState([]);
  const [entityToBlacklist, setEntityToBlacklist] = useState(null);
  const [blacklistedEntityStartDate, setBlacklistedEntityStartDate] = useState('');
  const [blacklistedEntityEndDate, setBlacklistedEntityEndDate] = useState('');

  const getBlacklistedEntities = () => {
    API.get(`/campblacklist?campaign_id=${campaignID}`).then((response) => {
      setBlacklistedEntities(response.data);
    });
  };

  const [openBlacklistedDialog, setOpenBlacklistedDialog] = useState(false);

  const handleOpenBlacklistedDialog = () => {
    setOpenBlacklistedDialog(true);
  };

  const handleCloseBlacklistedDialog = () => {
    setEntityToBlacklist(null);
    setBlacklistedEntityStartDate('');
    setBlacklistedEntityEndDate('');
    setOpenBlacklistedDialog(false);
  };

  const addBlacklistedEntity = (e) => {
    e.preventDefault();

    const data = {
      campaign_id: campaignID,
      entity_id: entityToBlacklist.id,
      start_date: blacklistedEntityStartDate ? dayjs(blacklistedEntityStartDate).format('YYYY/MM/DD') : null,
      end_date: blacklistedEntityEndDate ? dayjs(blacklistedEntityEndDate).format('YYYY/MM/DD') : null,
    };

    API.post(`/campblacklist`, data)
      .then((response) => {
        getBlacklistedEntities();
        handleCloseBlacklistedDialog();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  const [openDeleteBlacklistedEntity, setOpenDeleteBlacklistedEntity] = useState(false);
  const [entityToWhitelist, setEntityToWhitelist] = useState(null);

  const handleOpenDeleteBlacklistedEntity = (entity) => {
    setEntityToWhitelist(entity);
    setOpenDeleteBlacklistedEntity(true);
  };

  const handleCloseDeleteBlacklistedEntity = () => {
    setEntityToWhitelist(null);
    setOpenDeleteBlacklistedEntity(false);
  };

  const handleRemoveBlacklistedEntity = (e) => {
    e.preventDefault();

    API.delete(`/campblacklist/${entityToWhitelist.id}`)
      .then((response) => {
        getBlacklistedEntities();
        handleCloseDeleteBlacklistedEntity();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };
  /* ------------------------- Rates Table Pagination ------------------------- */

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(is600Screen ? 10 : 5);

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - displayedRates.length) : 0;

  // change page
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // change rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  // get user data
  useEffect(() => {
    let data;

    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));

    if (data) {
      const _currentProfile = data.roles.filter((role) => role.last_used);
      setCurrentProfile(_currentProfile);
    }
  }, []);

  if (currentProfile && currentProfile[0].prof_type_name !== 'ADMIN') return <Navigate to={{ pathname: '/' }} />;

  if (!campaignID) return <Navigate to={{ pathname: '/404' }} />;

  const handleCloseEntitiesFilterDialog = () => {
    setOpenEntitiesFilter(false);
  };

  const getSelectedEntity = (selectedEntity) => {
    if (tabValue === 1) {
      setSelectedEntity(selectedEntity);
    } else {
      setEntityToBlacklist(selectedEntity);
    }
  };

  return (
    <>
      <Helmet>
        <title>Dettagli Campagna</title>
      </Helmet>

      <Container maxWidth="xl" sx={{ height: '100%' }}>
        <EntitiesDialog
          openDialog={openEntitiesFilter}
          closeDialog={handleCloseEntitiesFilterDialog}
          outputEntity={getSelectedEntity}
          context={'campaign'}
        />

        <Grid item xs={12}>
          {/* Delete Campaign Button */}
          <Stack direction="row" justifyContent="end" alignItems="center">
            <RedOutlinedButton
              disabled={!apiCalled || rates.length !== 0}
              onClick={handleOpenDeleteCampaign}
              customStyles={{ display: canDeleteCampaign ? 'flex' : 'none' }}
            >
              Elimina Campagna
            </RedOutlinedButton>
          </Stack>

          {/* Delete Campaign Dialog */}
          <Dialog fullWidth open={openDeleteCampaign} onClose={() => {}}>
            <DialogTitle sx={{ textAlign: 'center' }}>Eliminazione Campagna</DialogTitle>
            <form onSubmit={deleteCampaign}>
              <DialogContent>
                {rates.length > 0 ? (
                  <Typography>La campagna non può essere eliminata</Typography>
                ) : (
                  <Typography>Sei sicuro di voler eliminale la campagna '{campaignInitialUrl}'?</Typography>
                )}
              </DialogContent>
              <DialogActions>
                <DefaultOutlinedButton onClick={handleCloseDeleteCampaign}>Annulla</DefaultOutlinedButton>
                <RedOutlinedButton disabled={!apiCalled || rates.length !== 0} type="submit">
                  Elimina
                </RedOutlinedButton>
              </DialogActions>
            </form>
          </Dialog>
        </Grid>

        <Box width="100%" height="100%">
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs variant="fullWidth" value={tabValue} onChange={handleChangeTab} aria-label="basic tabs example">
              <Tab label="Anagrafica" {...a11yProps(0, 'thriple')} icon={<AssignmentIcon />} iconPosition="start" />
              <Tab label="Tariffe" {...a11yProps(1, 'thriple')} icon={<PriceChangeIcon />} iconPosition="start" />
              <Tab
                label="Entità Bloccate"
                {...a11yProps(1, 'thriple')}
                icon={<DoDisturbOnIcon />}
                iconPosition="start"
              />
            </Tabs>
          </Box>
          {/* Campaign info */}
          <MultipleTabPanel value={tabValue} index={0}>
            {errorMessage.length === 0 ? (
              apiCalled ? (
                <Grid container spacing={3} sx={{ height: '100%' }}>
                  <Grid item xs={12}>
                    <Card sx={{ p: '24px' }}>
                      <form onSubmit={submitModify}>
                        <Grid container spacing={3}>
                          {/* Campaign name */}
                          <Grid item xs={8}>
                            <CustomTextField
                              required
                              disabled={!edit}
                              name="editCampaignName"
                              label="Nome Campagna"
                              defaultValue={campaignDescription}
                              onChange={(e) => setCampaignDescription(e.currentTarget.value)}
                            />
                          </Grid>

                          <Grid item xs={4}>
                            <CustomTextField
                              required
                              disabled={!edit}
                              inputProps={{ maxLength: 4 }}
                              name="editCampaignPrefix"
                              label="Prefisso Campagna"
                              defaultValue={campaignPrefix}
                              onChange={(e) => validatePhoneNumber(e, setCampaignPrefix)}
                            />
                          </Grid>

                          {/* campaign start date */}
                          <Grid item xs={12} md={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                              <CustomDatePicker
                                required
                                disabled={!edit}
                                name="editCampaignStartDate"
                                label="Data Inizio *"
                                value={dayjs(campaignStartDate)}
                                onChange={(date) => setCampaignStartDate(date)}
                                maxDate={dayjs(campaignEndDate)}
                              />
                            </LocalizationProvider>
                          </Grid>

                          {/* Campaign end date */}
                          <Grid item xs={12} md={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                              <CustomDatePicker
                                required
                                disabled={!edit}
                                name="editCampaignEndDate"
                                label="Data Fine *"
                                value={dayjs(campaignEndDate)}
                                onChange={(date) => setCampaignEndDate(date)}
                                minDate={dayjs(campaignStartDate)}
                              />
                            </LocalizationProvider>
                          </Grid>

                          {/* Start registration date */}
                          <Grid item xs={12} md={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                              <CustomDatePicker
                                required
                                disabled={!edit}
                                label="Data Inizio Registrazioni *"
                                name="editCampaignStartRegDate"
                                value={dayjs(campaignStartRegDate)}
                                onChange={(date) => setCampaignStartRegDate(date)}
                                maxDate={dayjs(campaignEndRegDate)}
                              />
                            </LocalizationProvider>
                          </Grid>

                          {/* End registration date */}
                          <Grid item xs={12} md={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                              <CustomDatePicker
                                required
                                disabled={!edit}
                                name="editCampaignEndRegDate"
                                label="Data Fine Registrazioni *"
                                value={dayjs(campaignEndRegDate)}
                                onChange={(date) => setCampaignEndRegDate(date)}
                                minDate={dayjs(campaignStartRegDate)}
                              />
                            </LocalizationProvider>
                          </Grid>

                          {/* Campaign status */}
                          <Grid item xs={12}>
                            <FormControl disabled={!edit}>
                              <FormLabel>Stato Campagna *</FormLabel>
                              <RadioGroup
                                row
                                aria-labelledby="demo-controlled-radio-buttons-group"
                                value={campaignActive}
                                onChange={handleChangeActive}
                              >
                                <FormControlLabel value={1} control={<Radio />} label="Attiva" />
                                <FormControlLabel value={0} control={<Radio />} label="Non Attiva" />
                              </RadioGroup>
                            </FormControl>
                          </Grid>

                          {/* Campaign type (PD / GD) */}
                          <Grid item xs={12}>
                            <FormControl disabled={!edit}>
                              <FormLabel>Tipo Campagna *</FormLabel>
                              <RadioGroup row value={campaignType} onChange={(e) => setCampaignType(e.target.value)}>
                                <FormControlLabel value="PD" control={<Radio />} label="Partito Democratico" />
                                <FormControlLabel value="GD" control={<Radio />} label="Giovani Democratici" />
                              </RadioGroup>
                            </FormControl>
                          </Grid>

                          {/* Submit */}
                          <Grid item xs={12} sx={{ display: canCreateCampaign ? 'block' : 'none' }}>
                            <Stack direction="row" justifyContent="end" gap="12px">
                              {edit && (
                                <DefaultOutlinedButton onClick={handleCloseEditButtonClick}>
                                  Annulla
                                </DefaultOutlinedButton>
                              )}
                              <DefaultContainedButton type="submit">
                                {edit ? 'Salva' : 'Modifica'}
                              </DefaultContainedButton>
                            </Stack>
                          </Grid>
                        </Grid>
                      </form>
                    </Card>
                  </Grid>
                </Grid>
              ) : (
                // Loading
                LoadingUI()
              )
            ) : (
              // Error handler
              ErrorUI(errorMessage)
            )}
          </MultipleTabPanel>

          {/* Rates */}
          <MultipleTabPanel value={tabValue} index={1}>
            {errorMessage.length === 0 ? (
              ratesApiCalled ? (
                <Grid container spacing={3}>
                  <Grid item xs={12} sx={{ paddingTop: '8px !important', marginTop: '24px' }}>
                    <Stack direction="row" justifyContent="end" gap="10px">
                      <DefaultContainedButton
                        onClick={handleOpenNewRate}
                        customStyles={{ display: canCreateCampaign ? 'flex' : 'none' }}
                        startIcon={<AddIcon />}
                      >
                        Nuova Tariffa
                      </DefaultContainedButton>
                      <DefaultContainedButton
                        onClick={handleOpenImportRates}
                        customStyles={{ display: canCreateCampaign ? 'flex' : 'none' }}
                        startIcon={<ImportExportIcon />}
                      >
                        Importa Tariffe
                      </DefaultContainedButton>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      {/* New Rate Dialog */}
                      <Dialog fullWidth open={openNewRate} onClose={() => {}}>
                        <DialogTitle sx={{ textAlign: 'center' }}>Nuova Tariffa</DialogTitle>

                        {staticDataApiCalled ? (
                          <form onSubmit={newRate}>
                            <DialogContent>
                              <Grid container spacing={3}>
                                {/* Type */}
                                <Grid item xs={12}>
                                  <CustomAutocomplete
                                    required
                                    options={rateTypes}
                                    variant="outlined"
                                    renderInput={(params) => <TextField {...params} label="Tipo *" />}
                                    getOptionLabel={(option) => option.description}
                                    onChange={(e, value) => (value ? setNewRateType(value) : setNewRateType(null))}
                                  />
                                </Grid>

                                {newRateType && newRateType.name === 'E' && (
                                  <Grid container spacing={3} sx={{ marginTop: '0', marginLeft: '0' }}>
                                    {/* Entity */}
                                    <Grid item xs={12}>
                                      {!selectedEntity ? (
                                        <DefaultOutlinedButton
                                          customStyles={{ width: '100%' }}
                                          onClick={() => setOpenEntitiesFilter(!openEntitiesFilter)}
                                        >
                                          Cerca Entità
                                        </DefaultOutlinedButton>
                                      ) : (
                                        <Grid container spacing={3}>
                                          <Grid item xs={12}>
                                            <CustomTextField
                                              InputProps={{
                                                endAdornment: (
                                                  <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                                                    <Tooltip title="Modifica" placement="left" arrow>
                                                      <EditIcon
                                                        onClick={() => setOpenEntitiesFilter(!openEntitiesFilter)}
                                                      />
                                                    </Tooltip>
                                                  </InputAdornment>
                                                ),
                                              }}
                                              value={selectedEntity.name}
                                              label="Entità *"
                                            />
                                          </Grid>
                                        </Grid>
                                      )}
                                    </Grid>
                                  </Grid>
                                )}

                                {newRateType && newRateType.name === 'C' && (
                                  // Position
                                  <Grid item xs={12}>
                                    <CustomAutocomplete
                                      disabled={newRateType.name !== 'C'}
                                      required
                                      options={positions}
                                      variant="outlined"
                                      renderInput={(params) => <TextField {...params} label="Carica" />}
                                      getOptionLabel={(option) => option.name}
                                      onChange={(e, value) => setNewRatePosition(value)}
                                    />
                                  </Grid>
                                )}

                                {newRateType && newRateType.name === 'ET' && (
                                  <Grid item xs={12}>
                                    <CustomAutocomplete
                                      disabled={newRateType.name !== 'ET'}
                                      required
                                      options={entityTypes}
                                      variant="outlined"
                                      renderInput={(params) => <TextField {...params} label="Tipo Entità" />}
                                      getOptionLabel={(option) => option.name}
                                      onChange={(e, value) => setNewRateEntityType(value)}
                                    />
                                  </Grid>
                                )}

                                {/* Name */}
                                <Grid item xs={12}>
                                  <CustomTextField
                                    required
                                    name="newRateName"
                                    onChange={(e) => setNewRateName(e.currentTarget.value)}
                                    value={newRateName}
                                    label="Nome Tariffa"
                                  />
                                </Grid>

                                {/* Price */}
                                <Grid item xs={12}>
                                  <CustomTextField
                                    required
                                    type="number"
                                    name="newRatePrice"
                                    onChange={(e) => setNewRatePrice(e.currentTarget.value)}
                                    value={newRatePrice}
                                    label="Prezzo"
                                  />
                                </Grid>

                                <Grid item xs={12}>
                                  <CustomTextField
                                    required
                                    type="number"
                                    name="newRateDiscountedPrice"
                                    onChange={(e) => setNewRateDiscountedPrice(e.currentTarget.value)}
                                    value={newRateDiscountedPrice}
                                    label="Sconto Giovani U30"
                                  />
                                </Grid>

                                {/* Currency */}
                                <Grid item xs={12}>
                                  <CustomTextField
                                    required
                                    select
                                    onChange={(e, value) => setNewRateCurr(value.props.value)}
                                    value={newRateCurr}
                                    label="Valuta"
                                  >
                                    {currencies.map((currency) => (
                                      <MenuItem key={currency.value} value={currency.value}>
                                        {currency.value}
                                      </MenuItem>
                                    ))}
                                  </CustomTextField>
                                </Grid>

                                {/* User Modifiable */}
                                <Grid item xs={12}>
                                  <FormControl>
                                    <FormLabel>Modificabile dall'Utente</FormLabel>
                                    <RadioGroup row value={newRateModifiable} onChange={handleNewRateModifiable}>
                                      <FormControlLabel value="1" control={<Radio />} label="Modificabile" />
                                      <FormControlLabel value="0" control={<Radio />} label="Non Modificabile" />
                                    </RadioGroup>
                                  </FormControl>
                                </Grid>
                              </Grid>
                            </DialogContent>
                            <DialogActions>
                              <DefaultOutlinedButton onClick={handleCloseNewRate}>Chiudi</DefaultOutlinedButton>
                              <DefaultContainedButton type="submit">Crea</DefaultContainedButton>
                            </DialogActions>

                          </form>
                        ) : (
                          LoadingUI()
                        )}
                        {!staticDataApiCalled && (
                          <DialogActions>
                            <DefaultOutlinedButton onClick={handleCloseNewRate}>Chiudi</DefaultOutlinedButton>
                          </DialogActions>
                        )}
                      </Dialog>

                      <Dialog fullWidth open={openImportRates} onClose={() => {}}>
                        <DialogTitle sx={{ textAlign: 'center' }}>Importa Tariffe</DialogTitle>

                        <form onSubmit={importRates}>
                          <DialogContent>
                            {campaignListApiCalled ? (
                              <Grid container spacing={3}>
                                <Grid item xs={12}>
                                  <Typography variant="span">
                                    Seleziona la campagna da cui vuoi importare le tariffe
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <FormControl color="secondary" sx={{ width: '100%' }}>
                                    <InputLabel>Campagna</InputLabel>
                                    <Select
                                      sx={{ width: '100%' }}
                                      value={selectedImportCampaign}
                                      label="Campagna"
                                      onChange={(e) => setSelectedImportCampaign(e.target.value)}
                                    >
                                      {campaignList.map((campaign) => (
                                        <MenuItem key={campaign.id} value={campaign.id}>
                                          {campaign.description}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>
                              </Grid>
                            ) : (
                              LoadingUI()
                            )}
                          </DialogContent>

                          <DialogActions>
                            <DefaultOutlinedButton onClick={handleCloseImportRates}>Chiudi</DefaultOutlinedButton>
                            <DefaultContainedButton type="submit">Importa</DefaultContainedButton>
                          </DialogActions>
                        </form>
                      </Dialog>
                    </Stack>
                  </Grid>

                  <Grid item xs={12}>
                    <Card>
                      {/* Filter Tabs */}
                      <Tabs
                        value={value}
                        onChange={handleChangeFilterTab}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        textColor="secondary"
                        indicatorColor="secondary"
                        sx={{ boxShadow: 'rgba(145, 158, 171, 0.08) 0px -2px 0px 0px inset', padding: '0px 24px 0' }}
                      >
                        <Tab
                          disableRipple
                          icon={
                            <Typography
                              sx={{
                                backgroundColor: value === 0 ? '#000000' : '#edeff1',
                                borderRadius: '6px',
                                color: value === 0 ? '#ffffff' : '#637381',
                                padding: '2px 8px',
                                fontSize: '1em',
                                fontWeight: 'bold',
                              }}
                            >
                              {rates.length}
                            </Typography>
                          }
                          iconPosition="end"
                          label="Tutte"
                        />
                        <Tab
                          disableRipple
                          icon={
                            <Typography
                              sx={{
                                backgroundColor: value === 1 ? '#22c55e' : '#dbf6e5',
                                borderRadius: '6px',
                                color: value === 1 ? '#dbf6e5' : '#118d57',
                                padding: '2px 8px',
                                fontSize: '1em',
                                fontWeight: 'bold',
                              }}
                            >
                              {positionRates.length}
                            </Typography>
                          }
                          iconPosition="end"
                          label="Carica"
                        />
                        <Tab
                          disableRipple
                          icon={
                            <Typography
                              sx={{
                                backgroundColor: value === 2 ? '#00b8d9' : '#d6f4f9',
                                borderRadius: '6px',
                                color: value === 2 ? '#ffffff' : '#1177a4',
                                padding: '2px 8px',
                                fontSize: '1em',
                                fontWeight: 'bold',
                              }}
                            >
                              {entityRates.length}
                            </Typography>
                          }
                          iconPosition="end"
                          label="Entità"
                        />
                        <Tab
                          disableRipple
                          icon={
                            <Typography
                              sx={{
                                backgroundColor: value === 3 ? '#ff5630' : '#ffe4de',
                                borderRadius: '6px',
                                color: value === 3 ? '#ffffff' : '#b71d18',
                                padding: '2px 8px',
                                fontSize: '1em',
                                fontWeight: 'bold',
                              }}
                            >
                              {freeRates.length}
                            </Typography>
                          }
                          iconPosition="end"
                          label="Libere"
                        />
                      </Tabs>

                      {/*  Table */}
                      <TableContainer sx={{ padding: '24px 24px 6px' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Nome Tariffa</TableCell>
                              <TableCell>Prezzo</TableCell>
                              <TableCell>Sconto Giovani U30</TableCell>
                              <TableCell>Valuta</TableCell>
                              <TableCell />
                            </TableRow>
                          </TableHead>
                          {displayedRates.length > 0 ? (
                            <TableBody>
                              {/* Edit Dialog */}
                              <Dialog open={openEditRate} fullWidth onClose={() => {}}>
                                <DialogTitle sx={{ textAlign: 'center' }}>Modifica Tariffa</DialogTitle>
                                <form onSubmit={editRate}>
                                  {rateToEditLoading ? (
                                    LoadingUI()
                                  ) : (
                                    <DialogContent>
                                      <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                          <CustomTextField
                                            InputProps={{
                                              readOnly: true,
                                            }}
                                            label="Tipo Tariffa"
                                            value={rateToEditRateTypeDescription}
                                          />
                                        </Grid>

                                        {rateToEditType.name === 'C' && (
                                          <Grid item xs={12}>
                                            <CustomAutocomplete
                                              required
                                              options={positions}
                                              value={rateToEditPosition}
                                              isOptionEqualToValue={(option, value) => option.id === value.id}
                                              getOptionLabel={(option) => option.name}
                                              renderOption={(props, option) => (
                                                <Box component="li" {...props} key={option.id}>
                                                  {option.name}
                                                </Box>
                                              )}
                                              onChange={(e, value) => {
                                                setRateToEditPosition(value);
                                              }}
                                              renderInput={(params) => (
                                                <TextField {...params} label="Carica" variant="outlined" />
                                              )}
                                            />
                                          </Grid>
                                        )}

                                        {rateToEditType.name === 'E' && (
                                          <Grid item xs={12}>
                                            <CustomTextField
                                              InputProps={{
                                                endAdornment: (
                                                  <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                                                    <Tooltip title="Modifica" placement="left" arrow>
                                                      <EditIcon
                                                        onClick={() => setOpenEntitiesFilter(!openEntitiesFilter)}
                                                      />
                                                    </Tooltip>
                                                  </InputAdornment>
                                                ),
                                              }}
                                              label="Entità"
                                              value={rateToEditEntity.name}
                                            />
                                          </Grid>
                                        )}

                                        {rateToEditType.name === 'ET' && (
                                          <Grid item xs={12}>
                                            <CustomAutocomplete
                                              required
                                              options={entityTypes}
                                              value={rateToEditEntityType}
                                              getOptionLabel={(option) => option.name}
                                              isOptionEqualToValue={(option, value) => option.id === value.id}
                                              renderOption={(props, option) => (
                                                <Box component="li" {...props} key={option.id}>
                                                  {option.name}
                                                </Box>
                                              )}
                                              onChange={(e, value) => {
                                                setRateToEditEntityType(value);
                                              }}
                                              renderInput={(params) => (
                                                <TextField {...params} label="Tipo Entità" variant="outlined" />
                                              )}
                                            />
                                          </Grid>
                                        )}

                                        {/* Name */}
                                        <Grid item xs={12}>
                                          <CustomTextField
                                            required
                                            name="name"
                                            label="Nome Tariffa"
                                            defaultValue={rateToEditName}
                                            onChange={(e) => setRateToEditName(e.currentTarget.value)}
                                          />
                                        </Grid>

                                        {/* Price */}
                                        <Grid item xs={12}>
                                          <CustomTextField
                                            required
                                            type="number"
                                            name="price"
                                            defaultValue={rateToEditPrice}
                                            onChange={(e) => setRateToEditPrice(e.currentTarget.value)}
                                            label="Prezzo"
                                          />
                                        </Grid>

                                        {/* Price */}
                                        <Grid item xs={12}>
                                          <CustomTextField
                                            required
                                            type="number"
                                            name="discountedPrice"
                                            defaultValue={rateToEditDiscountedPrice}
                                            onChange={(e) => setRateToEditDiscountedPrice(e.currentTarget.value)}
                                            label="Sconto Giovani U30"
                                          />
                                        </Grid>

                                        {/* Currency */}
                                        <Grid item xs={12}>
                                          <CustomTextField
                                            required
                                            select
                                            name="currency"
                                            value={rateToEditCurr}
                                            onChange={(e, value) => setRateToEditCurr(value.props.value)}
                                            label="Valuta"
                                          >
                                            {currencies.map((currency) => (
                                              <MenuItem key={currency.value} value={currency.value}>
                                                {currency.value}
                                              </MenuItem>
                                            ))}
                                          </CustomTextField>
                                        </Grid>

                                        {/* Modifiable */}
                                        <Grid item xs={12}>
                                          <FormControl>
                                            <FormLabel>Modificabile dall'Utente</FormLabel>
                                            <RadioGroup
                                              row
                                              value={rateToEditModifiable}
                                              onChange={handleEditRateModifiable}
                                            >
                                              <FormControlLabel value="1" control={<Radio />} label="Modificabile" />
                                              <FormControlLabel
                                                value="0"
                                                control={<Radio />}
                                                label="Non Modificabile"
                                              />
                                            </RadioGroup>
                                          </FormControl>
                                        </Grid>
                                      </Grid>
                                    </DialogContent>
                                  )}

                                  <DialogActions>
                                    <DefaultOutlinedButton onClick={handleCloseEditRate}>Chiudi</DefaultOutlinedButton>
                                    {!rateToEditLoading && (
                                      <DefaultContainedButton type="submit" onClick={editRate}>
                                        Salva
                                      </DefaultContainedButton>
                                    )}
                                  </DialogActions>
                                </form>

                              </Dialog>

                              <DecisionDialog
                                openDialog={openDeleteRate}
                                dialogTitle={'Elimina carica'}
                                dialogDescription={`Sei sicuro di voler eliminare la tariffa: ${rateToDelete.name} ?`}
                                closeOperation={handleCloseDeleteRate}
                                requestOperation={handleDeleteRate}
                                requestText={'Elimina'}
                                closeText={'Chiudi'}
                                RequestButton={RedOutlinedButton}
                              />

                              {displayedRates
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((rate) => (
                                  <TableRow hover key={rate.id}>
                                    <TableCell>{rate.name}</TableCell>
                                    <TableCell>{rate.price}</TableCell>
                                    <TableCell>{rate.price_discounted}</TableCell>
                                    <TableCell>{rate.currency}</TableCell>
                                    <TableCell>
                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="end"
                                        sx={{ display: canCreateCampaign ? 'flex' : 'none' }}
                                      >
                                        {/* Edit Rate */}
                                        <Box>
                                          <Tooltip title="Modifica Tariffa" arrow placement="top">
                                            <Box>
                                              <CustomIconButton onClick={() => handleOpenEditRate(rate)}>
                                                <EditIcon />
                                              </CustomIconButton>
                                            </Box>
                                          </Tooltip>
                                        </Box>

                                        {/* Delete Position */}
                                        <Box>
                                          <Tooltip title="Elimina Tariffa" arrow placement="top">
                                            <Box>
                                              <CustomIconButton onClick={() => handleOpenDeleteRate(rate)}>
                                                <DeleteIcon />
                                              </CustomIconButton>
                                            </Box>
                                          </Tooltip>
                                        </Box>
                                      </Stack>
                                    </TableCell>
                                  </TableRow>
                                ))}

                              {emptyRows > 0 && (
                                <TableRow style={{ height: 55 * emptyRows }}>
                                  <TableCell colSpan={5} />
                                </TableRow>
                              )}
                            </TableBody>
                          ) : (
                            // No Rates View
                            <TableBody>
                              <TableRow>
                                <TableCell colSpan={5} align="center">
                                  <Typography>Nessuna tariffa disponibile</Typography>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          )}

                          {/*  pagination */}
                          <TableFooter>
                            <TableRow>
                              <ThemeProvider theme={theme}>
                                <TablePagination
                                  sx={{ borderBottom: 'none' }}
                                  rowsPerPageOptions={[]}
                                  colSpan={5}
                                  count={displayedRates.length}
                                  rowsPerPage={rowsPerPage}
                                  page={page}
                                  onPageChange={handleChangePage}
                                  onRowsPerPageChange={handleChangeRowsPerPage}
                                  labelRowsPerPage="Righe per Pagina"
                                  ActionsComponent={TablePaginationActions}
                                />
                              </ThemeProvider>
                            </TableRow>
                          </TableFooter>
                        </Table>
                      </TableContainer>
                    </Card>
                  </Grid>
                </Grid>
              ) : (
                // Loading
                LoadingUI()
              )
            ) : (
              // Error handler
              ErrorUI(errorMessage)
            )}
          </MultipleTabPanel>

          <MultipleTabPanel value={tabValue} index={2}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                marginBottom: '24px',
                paddingTop: '8px',
              }}
            >
              <DefaultContainedButton onClick={handleOpenBlacklistedDialog}>Blocca Entità</DefaultContainedButton>
            </Box>

            <Dialog open={openBlacklistedDialog} onClose={() => {}} fullWidth>
              <DialogTitle>Blocca Entità</DialogTitle>
              <DialogContent>
                <Grid container spacing={3} sx={{ marginTop: '8px' }}>
                  {/* Entity */}
                  <Grid item xs={12}>
                    {!entityToBlacklist ? (
                      <DefaultOutlinedButton
                        customStyles={{ width: '100%' }}
                        onClick={() => setOpenEntitiesFilter(!openEntitiesFilter)}
                      >
                        Cerca Entità
                      </DefaultOutlinedButton>
                    ) : (
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <CustomTextField
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                                  <Tooltip title="Modifica" placement="left" arrow>
                                    <EditIcon onClick={() => setOpenEntitiesFilter(!openEntitiesFilter)} />
                                  </Tooltip>
                                </InputAdornment>
                              ),
                            }}
                            value={entityToBlacklist.name}
                            label="Entità *"
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                      <CustomDatePicker
                        required
                        label="Data Inizio"
                        onChange={(date) => setBlacklistedEntityStartDate(date)}
                        maxDate={dayjs(blacklistedEntityEndDate)}
                      />
                    </LocalizationProvider>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                      <CustomDatePicker
                        required
                        label="Data Fine"
                        onChange={(date) => setBlacklistedEntityEndDate(date)}
                        minDate={dayjs(blacklistedEntityStartDate)}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <DefaultOutlinedButton onClick={handleCloseBlacklistedDialog}>Chiudi</DefaultOutlinedButton>
                <DefaultContainedButton onClick={addBlacklistedEntity}>Blocca</DefaultContainedButton>
              </DialogActions>
            </Dialog>

            <Card sx={{ padding: '24px' }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Tipo Entità</TableCell>
                      <TableCell>Nome Entità</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>

                  {blacklistedEntities.length > 0 ? (
                    <TableBody>
                      {blacklistedEntities.map((entity) => (
                        <TableRow key={entity.id}>
                          {/* <TableCell>{entity.entity.entity_type.name}</TableCell>
                           */}
                          <TableCell>{entityTypeLabel(entity.entity, true)}</TableCell>

                          <TableCell>{entity.entity.name}</TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" justifyContent="end">
                              <Tooltip title="Rimuovi Entità" arrow placement="top">
                                <Box>
                                  <CustomIconButton onClick={() => handleOpenDeleteBlacklistedEntity(entity)}>
                                    <DeleteIcon />
                                  </CustomIconButton>
                                </Box>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  ) : (
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={3} sx={{ textAlign: 'center' }}>
                          Nessuna entità bloccata
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}

                  <Dialog open={openDeleteBlacklistedEntity} fullWidth onClose={() => {}}>
                    <DialogTitle>Rimuovi entità dalla blacklist</DialogTitle>
                    <DialogContent>
                      {`Sei sicuro di voler rimuovere l'entità "${entityToWhitelist?.entity?.name}" dalle entità bloccate?`}
                    </DialogContent>
                    <DialogActions>
                      <DefaultOutlinedButton onClick={handleCloseDeleteBlacklistedEntity}>Chiudi</DefaultOutlinedButton>
                      <DefaultContainedButton onClick={handleRemoveBlacklistedEntity}>Rimuovi</DefaultContainedButton>
                    </DialogActions>
                  </Dialog>
                </Table>
              </TableContainer>
            </Card>
          </MultipleTabPanel>
        </Box>

      </Container>
    </>
  );
}
